import Cookies from 'js-cookie';
import qs from 'qs';

import { toHttpError } from '@bonusly/http';
import { getToken } from '@bonusly/who-am-i';

import { FetchHttpError, HttpClient, HttpRequestConfig, HttpResponse } from './types';

type CreateHttpClientOptions = {
	onUnauthorized?: () => void;
};

function buildUrl(baseURL: string, path: string, config?: HttpRequestConfig): string {
	const effectiveBase = config?.baseURL ?? baseURL;
	const url = path.startsWith('http') ? path : `${effectiveBase.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
	if (config?.params) {
		const serialize = config.paramsSerializer ?? ((p: any) => qs.stringify(p, { arrayFormat: 'brackets' }));
		const queryString = serialize(config.params);
		return queryString ? `${url}?${queryString}` : url;
	}
	return url;
}

async function parseJson(response: Response): Promise<any> {
	const text = await response.text();
	if (!text) return undefined;
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
}

export const createHttpClient = (baseURL: string, options: CreateHttpClientOptions = {}): HttpClient => {
	async function request<T>(method: string, url: string, body?: any, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
		const token = await getToken();
		const csrfToken = Cookies.get('X-CSRF-TOKEN');

		const fullUrl = buildUrl(baseURL, url, config);

		const headers: Record<string, string> = {
			'X-BONUSLY-SOURCE': 'internal',
		};

		if (token) headers['Authorization'] = `Bearer ${token}`;
		if (csrfToken) headers['X-CSRF-TOKEN'] = csrfToken;

		if (body != null && !(body instanceof FormData)) {
			headers['Content-Type'] = 'application/json';
		}

		// Caller headers take precedence over defaults
		Object.assign(headers, config?.headers);

		// FormData requires Content-Type to be omitted so the browser sets the multipart boundary
		if (body instanceof FormData) {
			delete headers['Content-Type'];
		}

		const response = await fetch(fullUrl, {
			method,
			credentials: 'include',
			headers,
			body: body instanceof FormData ? body : body != null ? JSON.stringify(body) : undefined,
			signal: config?.signal,
		});

		if (!response.ok) {
			// Always parse error bodies as JSON regardless of responseType
			const errorData = await parseJson(response);
			const error = new FetchHttpError(response, errorData);
			if (toHttpError(error).isUnauthorized) {
				options.onUnauthorized?.();
			}
			throw error;
		}

		const responseType = config?.responseType;
		const data = responseType === 'blob' ? await response.blob() : responseType === 'text' ? await response.text() : await parseJson(response);

		return {
			data: data as T,
			status: response.status,
			statusText: response.statusText,
			headers: Object.fromEntries(response.headers.entries()),
			config: { method: method.toLowerCase(), headers },
		};
	}

	return {
		get: <T, R = HttpResponse<T>>(url: string, config?: HttpRequestConfig) => request<T>('GET', url, undefined, config) as Promise<R>,
		post: <T, R = HttpResponse<T>>(url: string, data?: any, config?: HttpRequestConfig) => request<T>('POST', url, data, config) as Promise<R>,
		put: <T, R = HttpResponse<T>>(url: string, data?: any, config?: HttpRequestConfig) => request<T>('PUT', url, data, config) as Promise<R>,
		patch: <T, R = HttpResponse<T>>(url: string, data?: any, config?: HttpRequestConfig) => request<T>('PATCH', url, data, config) as Promise<R>,
		delete: <T, R = HttpResponse<T>>(url: string, config?: HttpRequestConfig) => request<T>('DELETE', url, undefined, config) as Promise<R>,
		defaults: { baseURL },
	};
};
